import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useContentProtection } from '../hooks/useContentProtection';
import { 
  Menu, 
  X, 
  ShoppingCart, 
  User, 
  LogOut, 
  Upload, 
  LayoutDashboard,
  Heart,
  Package,
  ImageIcon
} from 'lucide-react';
import { useState } from 'react';
import NotificationBell from './NotificationBell';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const navLinks = [
  { path: '/gallery', label: 'Gallery' },
  { path: '/artists', label: 'Artists' },
];

function DesktopNav({ isActive }) {
  return (
    <nav className="hidden md:flex items-center gap-8">
      {navLinks.map(link => (
        <Link
          key={link.path}
          to={link.path}
          className={`nav-link ${isActive(link.path) ? 'active' : ''}`}
          data-testid={`nav-${link.label.toLowerCase()}`}
        >
          {link.label}
        </Link>
      ))}
    </nav>
  );
}

function UserMenu({ user, isAdmin, isArtist, onLogout }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button 
          className="flex items-center gap-2 p-2 text-[#A1A1AA] hover:text-[#F5F5F0] transition-colors"
          data-testid="user-menu-btn"
        >
          <User size={20} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="w-56 bg-[#121212] border-white/10"
      >
        <div className="px-3 py-2 border-b border-white/10">
          <p className="text-sm font-medium text-[#F5F5F0]">{user?.name}</p>
          <p className="text-xs text-[#71717A]">{user?.email}</p>
          <p className="text-xs text-[#D4AF37] capitalize mt-1">{user?.role}</p>
        </div>
        
        {isAdmin && (
          <DropdownMenuItem asChild>
            <Link to="/dashboard" className="flex items-center gap-2 cursor-pointer" data-testid="dashboard-link">
              <LayoutDashboard size={16} />
              Dashboard
            </Link>
          </DropdownMenuItem>
        )}
        
        {isArtist && (
          <DropdownMenuItem asChild>
            <Link to={`/artist/${user?.id}`} className="flex items-center gap-2 cursor-pointer" data-testid="my-profile-link">
              <User size={16} />
              My Profile
            </Link>
          </DropdownMenuItem>
        )}

        {(isArtist || isAdmin) && (
          <DropdownMenuItem asChild>
            <Link to="/my-works" className="flex items-center gap-2 cursor-pointer" data-testid="my-works-link">
              <ImageIcon size={16} />
              My Works
            </Link>
          </DropdownMenuItem>
        )}
        
        <DropdownMenuItem asChild>
          <Link to="/purchases" className="flex items-center gap-2 cursor-pointer" data-testid="purchases-link">
            <Package size={16} />
            My Purchases
          </Link>
        </DropdownMenuItem>

        <DropdownMenuItem asChild>
          <Link to="/favorites" className="flex items-center gap-2 cursor-pointer" data-testid="favorites-link">
            <Heart size={16} />
            My Favorites
          </Link>
        </DropdownMenuItem>
        
        <DropdownMenuSeparator className="bg-white/10" />
        
        <DropdownMenuItem 
          onClick={onLogout}
          className="flex items-center gap-2 cursor-pointer text-[#EF4444]"
          data-testid="logout-btn"
        >
          <LogOut size={16} />
          Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MobileMenu({ isActive, isAuthenticated, isArtist, isAdmin, itemCount, onClose, onLogout }) {
  return (
    <div className="md:hidden glass border-t border-white/10">
      <div className="px-4 py-4 space-y-4">
        {navLinks.map(link => (
          <Link
            key={link.path}
            to={link.path}
            className={`block py-2 ${isActive(link.path) ? 'text-[#D4AF37]' : 'text-[#A1A1AA]'}`}
            onClick={onClose}
          >
            {link.label}
          </Link>
        ))}
        
        {isAuthenticated ? (
          <>
            {(isArtist || isAdmin) && (
              <Link to="/upload" className="block py-2 text-[#A1A1AA]" onClick={onClose}>
                Upload Artwork
              </Link>
            )}
            {(isArtist || isAdmin) && (
              <Link to="/my-works" className="block py-2 text-[#A1A1AA]" onClick={onClose}>
                My Works
              </Link>
            )}
            <Link to="/cart" className="block py-2 text-[#A1A1AA]" onClick={onClose}>
              Cart ({itemCount})
            </Link>
            <Link to="/purchases" className="block py-2 text-[#A1A1AA]" onClick={onClose}>
              My Purchases
            </Link>
            <Link to="/favorites" className="block py-2 text-[#A1A1AA]" onClick={onClose}>
              My Favorites
            </Link>
            {isAdmin && (
              <Link to="/dashboard" className="block py-2 text-[#A1A1AA]" onClick={onClose}>
                Dashboard
              </Link>
            )}
            <button
              onClick={() => { onLogout(); onClose(); }}
              className="block py-2 text-[#EF4444]"
            >
              Logout
            </button>
          </>
        ) : (
          <>
            <Link to="/login" className="block py-2 text-[#A1A1AA]" onClick={onClose}>
              Sign In
            </Link>
            <Link to="/register" className="block py-2 text-[#D4AF37]" onClick={onClose}>
              Join
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

function SiteFooter() {
  return (
    <footer className="bg-[#0A0A0A] border-t border-white/10 py-16 lg:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          <div className="md:col-span-2">
            <Link to="/" className="font-serif text-2xl tracking-tight text-[#F5F5F0]">
              Mortal<span className="text-[#D4AF37]">Works</span>
            </Link>
            <p className="mt-4 text-[#71717A] max-w-md">
              A curated marketplace for authentic human-created art. 
              Photography, fine art, and films 
              from mortal hands and minds — never AI.
            </p>
          </div>
          
          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] text-[#A1A1AA] mb-4">Explore</h4>
            <ul className="space-y-2">
              <li><Link to="/gallery" className="text-[#71717A] hover:text-[#F5F5F0] transition-colors">Gallery</Link></li>
              <li><Link to="/artists" className="text-[#71717A] hover:text-[#F5F5F0] transition-colors">Artists</Link></li>
              <li><Link to="/gallery/photography" className="text-[#71717A] hover:text-[#F5F5F0] transition-colors">Photography</Link></li>
              <li><Link to="/gallery/fine_art" className="text-[#71717A] hover:text-[#F5F5F0] transition-colors">Fine Art</Link></li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] text-[#A1A1AA] mb-4">Join Us</h4>
            <ul className="space-y-2">
              <li><Link to="/register" className="text-[#71717A] hover:text-[#F5F5F0] transition-colors">Become an Artist</Link></li>
              <li><Link to="/membership" className="text-[#71717A] hover:text-[#F5F5F0] transition-colors">Buyer Membership</Link></li>
              <li><Link to="/privacy-policy" className="text-[#71717A] hover:text-[#F5F5F0] transition-colors">Privacy Policy</Link></li>
            </ul>
          </div>
        </div>
        
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-[#71717A] text-sm">
            &copy; {new Date().getFullYear()} Scott McGuire / mortalworks.art. All rights reserved.
          </p>
          <p className="text-[#71717A] text-xs max-w-md text-center md:text-right">
            All artworks are protected by copyright. Unauthorized use, copying, or screenshots for commercial purposes is prohibited.
          </p>
        </div>
      </div>
    </footer>
  );
}

export default function Layout() {
  const { user, isAuthenticated, isArtist, isAdmin, logout } = useAuth();
  const { itemCount } = useCart();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useContentProtection();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => {
    if (path === '/gallery') return location.pathname.startsWith('/gallery');
    return location.pathname === path;
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            <Link 
              to="/" 
              className="font-serif text-xl lg:text-2xl tracking-tight text-[#F5F5F0]"
              data-testid="logo-link"
            >
              Mortal<span className="text-[#D4AF37]">Works</span>
            </Link>

            <DesktopNav isActive={isActive} />

            {/* Desktop Actions */}
            <div className="hidden md:flex items-center gap-4">
              {isAuthenticated ? (
                <>
                  {(isArtist || isAdmin) && (
                    <Link to="/upload" className="btn-secondary flex items-center gap-2 text-sm" data-testid="upload-btn">
                      <Upload size={16} />
                      Upload
                    </Link>
                  )}
                  
                  <Link to="/cart" className="relative p-2 text-[#A1A1AA] hover:text-[#F5F5F0] transition-colors" data-testid="cart-btn">
                    <ShoppingCart size={20} />
                    {itemCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#D4AF37] text-[#0A0A0A] text-xs flex items-center justify-center">
                        {itemCount}
                      </span>
                    )}
                  </Link>

                  <NotificationBell />
                  <UserMenu user={user} isAdmin={isAdmin} isArtist={isArtist} onLogout={handleLogout} />
                </>
              ) : (
                <>
                  <Link to="/login" className="btn-secondary text-sm" data-testid="login-btn">Sign In</Link>
                  <Link to="/register" className="btn-primary text-sm" data-testid="register-btn">Join</Link>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 text-[#F5F5F0]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <MobileMenu
            isActive={isActive}
            isAuthenticated={isAuthenticated}
            isArtist={isArtist}
            isAdmin={isAdmin}
            itemCount={itemCount}
            onClose={() => setMobileMenuOpen(false)}
            onLogout={handleLogout}
          />
        )}
      </header>

      <main className="pt-16 lg:pt-20">
        <Outlet />
      </main>

      <SiteFooter />
    </div>
  );
}
